import React, { useState, useEffect, useRef } from 'react';
import ConnectCard from './ConnectCard';
import { BaseURL } from '../../../../Files/BaseURL';
import { CLIENT_ID } from '../../../../Files/PayPal_Cient_Id';
import { useNavigate } from "react-router-dom";

const ConnectAccount = ({ setConnect }) => {
  const [connectCardState, setConnectCardState] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [info, setInfo] = useState({});
  const paypalRef = useRef(null);
  const navigate = useNavigate();

  // Function to fetch user information
  const userInfo = async () => {
    const authToken = localStorage.getItem("p@token");
    const res = await fetch(`${BaseURL}/api/payment/account/pay-info`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        "auth-token": authToken,
      }
    });
    return await res.json();
  };

  useEffect(() => {
    const fetchInfo = async () => {
      const res = await userInfo();
      setInfo(res);
    };
    fetchInfo();
  }, []);

  // This function will simulate a click on the rendered PayPal button.
  const triggerPayPalFlow = () => {
    if (paypalRef.current) {
      // The PayPal SDK may render the button as a <button> element
      const paypalButton = paypalRef.current.querySelector('button');
      if (paypalButton) {
        paypalButton.click();
      } else {
        // If the button is rendered in an iframe or in a different structure,
        // try to simulate a click on the container instead.
        paypalRef.current.click();
      }
    } else {
      console.error("PayPal container reference not found.");
    }
  };

  useEffect(() => {
    // Load the PayPal SDK script
    const loadPayPalScript = () => {
      try {
        const existingScript = document.querySelector(`script[src="https://www.paypal.com/sdk/js?client-id=${CLIENT_ID}"]`);
        if (!existingScript) {
          const script = document.createElement("script");
          script.src = `https://www.paypal.com/sdk/js?client-id=${CLIENT_ID}`;
          script.async = true;
          script.onload = () => {
            setScriptLoaded(true);
            // Dispatch a custom event so that listeners know the script has loaded.
            window.dispatchEvent(new Event('paypalscriptloaded'));
          };
          script.onerror = () => console.error("Failed to load PayPal script.");
          document.body.appendChild(script);
        } else {
          setScriptLoaded(true);
        }
      } catch (error) {
        console.error("Error during PayPal script loading:", error.message);
      }
    };

    // Render the PayPal buttons in the designated container
    const renderPayPalButtons = (access_token, authToken) => {
      if (paypalRef.current) {
        window.paypal.Buttons({
          createVaultSetupToken: async () => {
            try {
              const result = await fetch(`${BaseURL}/api/payment/account/setup-token`, {
                method: "POST",
                headers: {
                  "Authorization": `Bearer ${access_token}`,
                  "Content-Type": "application/json",
                  "auth-token": authToken,
                },
              });
              if (!result.ok) {
                throw new Error(`Failed to create vault setup token: ${result.statusText}`);
              }
              const response = await result.json();
              return response.id;
            } catch (error) {
              console.error("Error during createVaultSetupToken:", error.message);
              throw error;
            }
          },
          onApprove: async ({ vaultSetupToken }) => {
            try {
              const response = await fetch(`${BaseURL}/api/payment/account/payment-token`, {
                method: "POST",
                body: JSON.stringify({ vaultSetupToken }),
                headers: {
                  "Authorization": `Bearer ${access_token}`,
                  "Content-Type": "application/json",
                  "auth-token": authToken,
                },
              });
              if (!response.ok) {
                throw new Error(`Failed to approve vault setup token: ${response.statusText}`);
              }
              // Navigate to /signin after the PayPal account is attached.
              navigate('/signin');
            } catch (error) {
              console.error("Error during onApprove:", error.message);
            }
          },
          onError: (error) => {
            console.error("An error occurred during PayPal button rendering:", error.message);
          }
        }).render(paypalRef.current);
      } else {
        console.error("PayPal container element is not available.");
      }
    };

    // Fetch tokens and then render the PayPal button
    const fetchTokensAndRenderButtons = async () => {
      try {
        const authToken = localStorage.getItem("p@token");
        const tokenResult = await fetch(`${BaseURL}/api/payment/account/paypal-tokens`, {
          method: "POST",
          headers: {
            "auth-token": authToken,
            "Content-Type": "application/json",
          }
        });
        if (!tokenResult.ok) {
          throw new Error(`Failed to fetch tokens: ${tokenResult.statusText}`);
        }
        const { access_token } = await tokenResult.json();

        if (window.paypal && scriptLoaded) {
          renderPayPalButtons(access_token, authToken);
        } else {
          window.addEventListener('paypalscriptloaded', () => {
            renderPayPalButtons(access_token, authToken);
          });
        }
      } catch (error) {
        console.error("Error during PayPal setup:", error.message);
      }
    };

    // Start by loading the script, then render the buttons when ready.
    loadPayPalScript();
    if (scriptLoaded) {
      fetchTokensAndRenderButtons();
    } else {
      window.addEventListener('paypalscriptloaded', fetchTokensAndRenderButtons);
    }

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('paypalscriptloaded', fetchTokensAndRenderButtons);
    };
  }, [scriptLoaded, navigate]);

  return (
    <>
      {/* Overlay for the Credit Card connection */}
      <div
        className={`${connectCardState ? "block" : "hidden"} fixed top-0 left-0 z-50 w-screen h-screen flex justify-center items-center`}
      >
        <div
          className="absolute w-full h-full z-10"
          onClick={() => {
            setConnect(false);
            setConnectCardState(false);
          }}
        ></div>
        <ConnectCard setConnect={setConnect} />
      </div>

      {/* Main container */}
      <div className="font-pop relative z-10 py-20">
        <div className="mx-6 bg-white max-w-md p-4 md:p-8 rounded-lg shadow-md">
          <h2 className="text-lg md:text-2xl font-pop font-semibold mb-4">Connect account</h2>
          <p className="text-gray-600 mb-6 text-sm md:text-base max-w-sm">
            Link your PayPal account or any other credit card
          </p>

          <div className="flex flex-col font-pop gap-4">
            {/* PayPal connection area */}
            <div
              className="relative flex flex-row border-2 items-center border-[#E9E9E9] py-4 px-6 rounded-lg gap-2 cursor-pointer"
              // The click on this container (or its overlay) will trigger the PayPal flow.
              
            >
              <img
                src="../../Assets/Accounts/Paypal.png"
                alt="PayPal"
                className=" w-6 md:w-8 h-6 md:h-8"
                onClick={triggerPayPalFlow}
              />
              <p className=" text-base md:text-lg font-medium" 
              onClick={triggerPayPalFlow}
              >
                
                PayPal {info?.AttachedPaypal && `(${info.AttachedPaypal.Email.substring(0, 4)} ****)`}
              </p>
              {/* Invisible overlay that contains the rendered PayPal button */}
              <div
                className="absolute w-full h-full cursor-pointer inset-0 opacity-0"
                // Also attach the click handler here so that if the click lands on the overlay, it triggers the flow.
                onClick={triggerPayPalFlow}
              >
                <div
                  ref={paypalRef}
                  className="h-full w-full flex flex-col justify-center items-center"
                ></div>
              </div>
            </div>

            {/* Credit Card connection area */}
            <div
              className="flex flex-row border-2 items-center border-[#E9E9E9] py-4 px-6 rounded-lg gap-2 cursor-pointer"
              onClick={() => setConnectCardState(true)}
            >
              <img
                src="../../Assets/Accounts/card.png"
                alt="Credit Card"
                className="w-6 md:w-8 h-6 md:h-8"
              />
              <p className="text-base md:text-lg font-medium">
                Credit Card {info?.AttachedCard && `(**** ${info.AttachedCard.LastDigits})`}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConnectAccount;
